import AddNewSession from '@/connection/sessions/add-new-session';
import { BLAdmin } from '@/types/profile.types';
import { BrandliveEvent, IEventTag } from '@/types/projects.types';
import { getDefaultFiresideSessionSettings, getDefaultSession } from './create-new-session';
import { BreakoutRoom, EBroadcastTypes, Session, SessionTypesEnum } from '@/types/session.types';
import { CreateSession } from '@/types/create-project.types';
import { LanguagesAbbr } from '@/types/translation.types';
import { NavMainItems } from '@/types/homepage.types';
import { GetDefaultMainNavItems, GetMultipleSessionsMainNavItems } from './create-event';
import { workingProjectDefaultLanguage } from './get-default-language';
import { saveWorkingProject, updateMostRecentActivity } from '@/store/slices/project';
import { useAppDispatch } from '@/store/hooks';
import { SaveProject } from '@/connection/projects/save-project';
import { ESessionFeatures } from '@/types/session-features.types';
import { TBroadcastTypePickerOptions } from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/broadcast-type-picker.types';

export const handleAddNewSession = async ({
  project,
  user,
  channel,
  defaultLanguage,
  dispatch,
  title,
  startTimestamp,
  endTimestamp,
  description,
  image,
  tracks,
  tags,
  selectedBroadcastType,
  saveAsynchronously,
  features,
  languages,
  firesides_hosts,
  breakoutRooms,
}: {
  project: BrandliveEvent;
  user: BLAdmin;
  channel: number;
  defaultLanguage: LanguagesAbbr;
  dispatch: ReturnType<typeof useAppDispatch>;
  title?: string;
  description?: string;
  startTimestamp?: number | null;
  endTimestamp?: number | null;
  image?: string | null;
  tracks?: string[];
  selectedBroadcastType?: TBroadcastTypePickerOptions;
  tags?: IEventTag[];
  features?: ESessionFeatures[];
  languages?: string[];
  firesides_hosts?: string[];
  breakoutRooms?: BreakoutRoom[];
  /**
   * saveAsynchronously useful if you need to wait for the save to finish, but not if you need to "debounce" saves
   * debouncing saves happens in the saveWorkingProject action middleware if saveAsynchronously is false
   * */
  saveAsynchronously?: boolean;
}): Promise<Session | null> => {
  if (!project || !user || !channel) return null;

  const date = new Date();
  date.setMinutes(Math.ceil(date.getMinutes() / 30) * 30 + 30, 0, 0); //round the current date up to the nearest 30min increment and then add 30min to that (09:15 rounds to 9:30. + 30min = 10:00)
  const duration = 60;

  const requriesStartChange = startTimestamp ? false : true;
  const requriesEndChange = endTimestamp ? false : true;
  const includeChat = !!features?.includes(ESessionFeatures.CHAT);

  let broadcastType = EBroadcastTypes.greenroom;
  let sessionType: SessionTypesEnum = SessionTypesEnum.broadcast;
  const isFireside = selectedBroadcastType === EBroadcastTypes.fireside;
  const isOndemand = selectedBroadcastType === SessionTypesEnum.onDemand;
  const isBreakout = selectedBroadcastType === SessionTypesEnum.breakoutRooms;
  if (isFireside) {
    sessionType = SessionTypesEnum.fireside;
    broadcastType = EBroadcastTypes.fireside;
  } else if (isBreakout) {
    sessionType = SessionTypesEnum.breakoutRooms;
  } else if (isOndemand) {
    sessionType = SessionTypesEnum.onDemand;
    broadcastType = EBroadcastTypes.rtmps;
  } else {
    if (selectedBroadcastType) {
      broadcastType = selectedBroadcastType;
    }
  }

  const sessionToSave: CreateSession = {
    // Notes:
    // - default language must be included in the session's languages (which is the language passed in as a prop)
    // - firesides can only have one language and that language must be the default language. But,
    // since we're hardcoding the type to broadcast, we can ignore that for now
    ...getDefaultSession(user, sessionType, defaultLanguage),
    languages: languages || [defaultLanguage],
    title: title || `My Session ${Date.now()}`,
    description: description || '',
    image,
    on_demand: !!isOndemand, // setting on_demand even though this field is technically deprecated
    replay_on: getReplayValue(sessionType),
    start_timestamp: startTimestamp || date.valueOf(),
    requires_time_change: {
      start_date: requriesStartChange,
      start_time: requriesStartChange,
      end_date: requriesEndChange,
      end_time: requriesEndChange,
    },
    tracks: tracks || [],
    end_timestamp: endTimestamp || (duration ? date.valueOf() + 1000 * 60 * duration : undefined),
    broadcast_type: broadcastType,
    session_type: sessionType,
    tags: (tags || []).map((tag) => tag.uuid),
    session_chat_enabled: includeChat,
    streaming_options: {
      test_stream: false,
      single_stream: true,
      auto_transcripts: !!features?.includes(ESessionFeatures.CAPTIONS),
      watermark: false,
    },
    ...(isFireside && firesides_hosts
      ? {
          firesides_hosts,
          fireside_session_settings: {
            settings: {
              ...getDefaultFiresideSessionSettings(),
              use_google_meet: true,
              google_meet_use_service_account: true,
            },
          },
        }
      : {}),
    ...(isBreakout && breakoutRooms?.length && firesides_hosts?.length
      ? {
          breakout_session: {
            hosts: firesides_hosts,
            use_google_meet: true,
            use_zoom: false,
            create_room_when_full: false,
            google_meet_use_service_account: true,
            rooms: breakoutRooms,
          },
        }
      : {}),
  };

  const session = await AddNewSession({
    eventUuid: project.uuid,
    session: sessionToSave,
    event: project.event,
    channel,
  });

  const leaderboardNavIcon = project?.homepage?.event_main_nav_items?.find(
    (ni) => ni.name === NavMainItems.Leaderboard
  );

  // hardcoding to true for now because this is currently only used in the case of adding a session from a single session event
  const addingSessionFromSingleSessionEvent = true;

  if (project?.sessions.length === 1 && project?.homepage?.homepage) {
    const updatedMainNavItems = addingSessionFromSingleSessionEvent
      ? GetMultipleSessionsMainNavItems(
          workingProjectDefaultLanguage(project),
          undefined,
          undefined,
          leaderboardNavIcon
        )
      : GetDefaultMainNavItems(workingProjectDefaultLanguage(project));

    const updatedProject: BrandliveEvent = {
      ...project,
      tags: [...(project?.tags || []), ...(tags || [])],
      sessions: [...project.sessions, session],
      homepage: {
        ...project.homepage,
        event_main_nav_items: updatedMainNavItems,
      },
      settings: {
        ...project.settings,
        display_homepage: true,
      },
    };
    if (saveAsynchronously) {
      await SaveProject(updatedProject);
    } else {
      dispatch(saveWorkingProject({ project: updatedProject }));
    }
  } else {
    const updatedProject: BrandliveEvent = {
      ...project,
      tags: [...(project?.tags || []), ...(tags || [])],
      sessions: [...project.sessions, session],
      settings: {
        ...project.settings,
        display_homepage: true,
      },
    };
    if (saveAsynchronously) {
      await SaveProject(updatedProject);
      dispatch(updateMostRecentActivity({ isPublish: false }));
    } else {
      dispatch(saveWorkingProject({ project: updatedProject }));
    }
  }

  return session;
};

function getReplayValue(sessionType: SessionTypesEnum): boolean | null {
  // used to initialize the replay toggle as true for determined session types
  // retains default null initial value for all other session types
  switch (sessionType) {
    case SessionTypesEnum.broadcast: {
      return true;
    }
    case SessionTypesEnum.fireside: {
      return true;
    }
    // returning null here to keep with the current data structure
    // new sessions are initially given a null value for this boolean until the toggle is interacted with
    default:
      return null;
  }
}
